import Swal from 'sweetalert2';
import 'animate.css';

interface NavigationConfirmationOptions {
  title?: string;
  text?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

export const NavigationConfirmation = {
  /**
   * Shows a navigation confirmation dialog with SweetAlert2
   * Simple logic: when there's data loaded, show confirmation before navigation
   * Returns a promise that resolves to true if user confirms, false if cancelled
   */
  confirm: async (options: NavigationConfirmationOptions = {}): Promise<boolean> => {
    const {
      title = 'Leave Page?',
      text = 'You have unsaved work. Are you sure you want to leave? Your progress will be lost.',
      confirmButtonText = 'Yes, Leave',
      cancelButtonText = 'Stay Here'
    } = options;

    const result = await Swal.fire({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      customClass: {
        container: 'fixed inset-0 z-50 overflow-y-auto bg-gray-900/50',
        popup: 'relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl transform p-6 max-w-md mx-auto mt-16 animate__animated',
        title: 'text-2xl font-bold text-gray-800 dark:text-white mb-4',
        htmlContainer: 'text-gray-600 dark:text-gray-300 text-base mb-6',
        actions: 'flex gap-3 justify-end mt-6',
        confirmButton: 'px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-all duration-150 ease-in-out transform hover:scale-105 animate__animated animate__fadeIn animate__faster',
        cancelButton: 'px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-150 ease-in-out transform hover:scale-105 animate__animated animate__fadeIn animate__faster',
      },
      showClass: {
        popup: 'animate__animated animate__zoomIn animate__faster',
        backdrop: 'swal2-backdrop-show animate__animated animate__fadeIn animate__faster'
      },
      hideClass: {
        popup: 'animate__animated animate__zoomOut animate__faster',
        backdrop: 'swal2-backdrop-hide animate__animated animate__fadeOut animate__faster'
      },
      backdrop: true,
      timer: undefined,
      timerProgressBar: false,
      allowOutsideClick: false,
      allowEscapeKey: true,
      allowEnterKey: true,
      focusConfirm: false,
      reverseButtons: false
    });

    return result.isConfirmed;
  },

  /**
   * Shows a simple navigation confirmation using window.confirm as fallback
   * Used for browser events where async/await isn't supported
   */
  simpleConfirm: (message?: string): boolean => {
    const defaultMessage = 'You have unsaved work. Are you sure you want to leave? Your progress will be lost.';
    return window.confirm(message || defaultMessage);
  }
};